// ─────────────────────────────────────────────────────────────────────────────
// Service auth API client (Phase 7.5).
//
// Thin wrappers around the 4 backend endpoints from Phase 7.4. Each function:
//   - Builds the request (including the right Authorization header)
//   - Parses the JSON response
//   - Throws a typed AuthApiError on non-2xx, with the server's `error` code
//   - On 2xx, returns the typed response
//
// We do NOT use the existing apiClient.ts here because:
//   - apiClient is for ETag-aware GETs of polled endpoints
//   - apiClient throws AuthRequiredError on 401, which would loop back into
//     the auth flow incorrectly (we want the form to surface "wrong password")
//
// The auth flow has its own error semantics: 401 from /login is INVALID_CREDS,
// not "your session expired". So separate transport.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  AuthSubsidiary,
  AuthBranch,
  AuthUser,
  AuthSessionInfo,
} from './authClient';

export class AuthApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  /** The whole error body — SETUP_REQUIRED carries the wizard's token in it. */
  body: Record<string, unknown> | null;
  constructor(status: number, code: string, details?: unknown, body: Record<string, unknown> | null = null) {
    super(`${code} (HTTP ${status})`);
    this.name = 'AuthApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.body = body;
  }
}

// ── /login ──────────────────────────────────────────────────────────────────

export interface LoginRequest {
  tenant_slug: string;
  username: string;
  password: string;
}

export interface LoginResponse {
  pending_token: string;
  user: AuthUser;
  available_subsidiaries: AuthSubsidiary[];
}

export async function loginRequest(body: LoginRequest): Promise<LoginResponse> {
  // Deliberately does NOT send x-erp-database.
  //
  // It used to send `GL_WEB_${tenant_slug}`, and pos-api prefers that header
  // over the central directory — so the client's guess decided which database
  // to open. That guess is only right when a tenant's slug happens to match
  // its database suffix. It does not for "matixpos", which is
  // GL_WEB_villashams_test: the header forced a database that does not exist,
  // the pool acquire failed, and the login screen reported "the server is
  // temporarily unavailable", blaming the server for a bad client guess.
  //
  // With no header, service-auth resolves the slug through dbo._Clients2 —
  // the same path the cashier and admin logins already take, neither of which
  // sends this header either.
  return postJson<LoginResponse>('/api/v1/service/auth/login', body);
}


// ── /select-subsidiary ──────────────────────────────────────────────────────

export interface SelectSubsidiaryResponse {
  pending_token: string;
  subsidiary: AuthSubsidiary;
  available_branches: AuthBranch[];
}

export async function selectSubsidiaryRequest(
  pendingToken: string,
  subsidiaryId: number,
): Promise<SelectSubsidiaryResponse> {
  return postJson<SelectSubsidiaryResponse>(
    '/api/v1/service/auth/select-subsidiary',
    { subsidiary_id: subsidiaryId },
    pendingToken,
  );
}

// ── /select-branch ──────────────────────────────────────────────────────────

export interface SelectBranchResponse {
  token: string;
  expires_at: string;
  session: AuthSessionInfo;
}

export async function selectBranchRequest(
  pendingToken: string,
  branchId: number,
): Promise<SelectBranchResponse> {
  return postJson<SelectBranchResponse>(
    '/api/v1/service/auth/select-branch',
    { branch_id: branchId },
    pendingToken,
  );
}

// ── /me ─────────────────────────────────────────────────────────────────────

export interface MeResponse {
  session: AuthSessionInfo;
}

export async function meRequest(sessionToken: string): Promise<MeResponse> {
  const resp = await fetch('/api/v1/service/auth/me', {
    method: 'GET',
    headers: {
      accept: 'application/json',
      authorization: `Bearer ${sessionToken}`,
    },
  });
  return parseJsonOrThrow<MeResponse>(resp);
}

// ── helpers ─────────────────────────────────────────────────────────────────

async function postJson<T>(
  path: string,
  body: unknown,
  bearer?: string,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    accept: 'application/json',
    ...extraHeaders,
  };
  if (bearer) headers.authorization = `Bearer ${bearer}`;

  const resp = await fetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  return parseJsonOrThrow<T>(resp);
}

async function parseJsonOrThrow<T>(resp: Response): Promise<T> {
  if (resp.ok) {
    return (await resp.json()) as T;
  }

  // The backend always returns JSON for known errors. For unexpected statuses
  // (502, network blips), the body might not be JSON — fall back to a generic.
  let body: { error?: string; details?: unknown } | null = null;
  try {
    body = (await resp.json()) as { error?: string; details?: unknown };
  } catch {
    // not JSON
  }
  const code = body?.error ?? `HTTP_${resp.status}`;
  throw new AuthApiError(resp.status, code, body?.details, body as Record<string, unknown> | null);
}
